import React, { useState } from "react";
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { 
  Brain, 
  Eye, 
  Zap, 
  DollarSign, 
  Clock, 
  BarChart3,
  CheckCircle,
  ArrowRight,
  Mail,
  Phone,
  MapPin,
  Menu,
  X
} from "lucide-react";

const VapourLanding = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleDemoClick = () => {
    window.open('https://huggingface.co/phronetic-ai/collections', '_blank');
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  return (
    <div className="dark-container">
      {/* Header */}
      <header className="dark-header">
        <div className="dark-logo">
          <span className="heading-2" style={{ color: '#00FFD1' }}>Vapour</span>
          <a href="https://nighthack.in" className="body-small mobile-hidden" style={{ color: '#4D4D4D', marginLeft: '8px' }}>by NightHack Labs</a>
        </div>
        
        {/* Desktop Navigation */}
        <nav className="dark-nav desktop-nav">
          <a href="#features" className="dark-nav-link">Features</a>
          <a href="#pricing" className="dark-nav-link">Pricing</a>
          <a href="#contact" className="dark-nav-link">Contact</a>
          <Button className="btn-primary" onClick={handleDemoClick}>
            See Demo
            <ArrowRight size={16} />
          </Button>
        </nav>

        {/* Mobile Menu Button */}
        <button className="mobile-menu-button" onClick={toggleMobileMenu}>
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <nav className="mobile-nav">
            <a href="#features" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>Features</a>
            <a href="#pricing" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>Pricing</a>
            <a href="#contact" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>Contact</a>
            <Button className="btn-primary mobile-cta" onClick={handleDemoClick} style={{ width: '100%', marginTop: '16px' }}>
              See Demo
              <ArrowRight size={16} />
            </Button>
          </nav>
        )}
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        {/* Enhanced Animated Background */}
        <div className="distillation-animation">
          {/* Large Model Network - Brain-like Structure */}
          <div className="large-network">
            {/* Neural clusters */}
            {[...Array(32)].map((_, i) => (
              <div key={i} className={`network-node node-${i}`} />
            ))}
            {/* Complex connections forming brain-like patterns */}
            {[...Array(45)].map((_, i) => (
              <div key={i} className={`network-connection connection-${i}`} />
            ))}
            {/* Pulsing data points */}
            {[...Array(12)].map((_, i) => (
              <div key={i} className={`data-pulse pulse-${i}`} />
            ))}
          </div>
          
          {/* Distillation Process - Data Stream */}
          <div className="distillation-flow">
            {/* Main flow particles */}
            {[...Array(16)].map((_, i) => (
              <div key={i} className={`flow-particle particle-${i}`} />
            ))}
            {/* Processing indicators */}
            {[...Array(6)].map((_, i) => (
              <div key={i} className={`process-indicator indicator-${i}`} />
            ))}
          </div>
          
          {/* Distilled Model - Geometric Precision */}
          <div className="distilled-network">
            {/* Geometric nodes */}
            {[...Array(8)].map((_, i) => (
              <div key={i} className={`distilled-node distilled-${i}`} />
            ))}
            {/* Precise connections */}
            {[...Array(12)].map((_, i) => (
              <div key={i} className={`distilled-connection distilled-conn-${i}`} />
            ))}
            {/* Output indicators */}
            {[...Array(4)].map((_, i) => (
              <div key={i} className={`output-node output-${i}`} />
            ))}
          </div>
          
          {/* Background Grid Pattern */}
          <div className="tech-grid">
            {[...Array(20)].map((_, i) => (
              <div key={i} className={`grid-line grid-${i}`} />
            ))}
          </div>
        </div>
        
        <div className="dark-content-container">
          <div style={{ padding: '160px 0 100px', textAlign: 'left', position: 'relative', zIndex: 2 }}>
            <Badge className="hero-badge glassmorphism-badge my-2">
              AI Model Distillation Experts
            </Badge>
            <h1 className="display-huge" style={{ marginBottom: '24px', maxWidth: '800px' }}>
              Distill Your Business Data Into 
              <span style={{ color: '#00FFD1' }}> Powerful AI Models</span>
            </h1>
            <p className="body-large" style={{ marginBottom: '24px', maxWidth: '600px', color: 'rgba(255, 255, 255, 0.85)' }}>
              Save costs and get started for the price of man-hours needed to distill your model. 
              Our experts understand your business and data to create distilled models with same or better accuracy.
            </p>
            
            {/* Cost Savings Examples */}
            <div style={{ marginBottom: '40px', padding: '24px', background: 'rgba(0, 255, 209, 0.08)', border: '1px solid rgba(0, 255, 209, 0.2)', borderRadius: '12px', backdropFilter: 'blur(10px)', maxWidth: '600px' }}>
              <div className="body-small" style={{ color: '#00FFD1', marginBottom: '8px', fontWeight: '600' }}>Typical Cost Savings</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', fontSize: '16px' }}>
                <div>
                  <div style={{ color: 'rgba(255, 255, 255, 0.7)' }}>OpenAI GPT-4 FT:</div>
                  <div style={{ color: '#FFFFFF', fontWeight: '500' }}>$30/10M tokens</div>
                </div>
                <div>
                  <div style={{ color: 'rgba(255, 255, 255, 0.7)' }}>Vapour Distilled:</div>
                  <div style={{ color: '#00FFD1', fontWeight: '600' }}>$3/10M tokens</div>
                </div>
              </div>
              <div style={{ marginTop: '12px', fontSize: '14px', color: 'rgba(255, 255, 255, 0.8)' }}>
                <strong style={{ color: '#00FFD1' }}>Up to 90% cost reduction</strong> with same or better accuracy
              </div>
            </div>
            <div className="hero-buttons">
              <Button className="btn-primary" onClick={handleDemoClick}>
                See Our Models In Action
                <ArrowRight size={20} />
              </Button>
              <a className="btn-secondary" href="https://koalendar.com/u/64wMZ2tCwbeNQ3aNUVktnNVyFKl2" target="_blank" >
                Schedule Consultation
                <Phone size={16} />
              </a>
            </div>
            <div className="hero-expertise">
              <div className="expertise-item">
                <div className="body-small" style={{ color: '#4D4D4D' }}>Expertise In</div>
                <div className="body-medium" style={{ color: '#FFFFFF' }}>Small Large Language Models</div>
              </div>
              <div className="expertise-item">
                <div className="body-small" style={{ color: '#4D4D4D' }}>Specialized In</div>
                <div className="body-medium" style={{ color: '#FFFFFF' }}>Vision Language Models</div>
              </div>
              <div className="expertise-item">
                <div className="body-small" style={{ color: '#4D4D4D' }}>Live Deployments</div>
                <div className="body-medium" style={{ color: '#FFFFFF' }}>Mixed Model Solutions</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features-section">
        <div className="dark-content-container">
          <div style={{ padding: '100px 0', textAlign: 'center' }}>
            <h2 className="display-medium" style={{ marginBottom: '16px' }}>
              Why Choose Vapour for Model Distillation?
            </h2>
            <p className="body-large" style={{ marginBottom: '60px', color: 'rgba(255, 255, 255, 0.85)', maxWidth: '600px', margin: '0 auto 60px' }}>
              Transform your business data into efficient AI models that deliver results at a fraction of the cost.
            </p>
            
            <div className="dark-grid">
              <Card className="feature-card glassmorphism-card">
                <CardHeader>
                  <div className="icon-container glassmorphism-icon">
                    <DollarSign size={24} style={{ color: '#00FFD1' }} />
                  </div>
                  <CardTitle className="heading-3">Cost-Effective Solutions</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="body-medium">
                    Pay only for the man-hours needed to distill your model. Significant cost savings compared to training from scratch or using expensive cloud APIs.
                  </CardDescription>
                </CardContent>
              </Card>

              <Card className="feature-card glassmorphism-card">
                <CardHeader>
                  <div className="icon-container glassmorphism-icon">
                    <BarChart3 size={24} style={{ color: '#00FFD1' }} />
                  </div>
                  <CardTitle className="heading-3">Same or Better Accuracy</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="body-medium">
                    Our distilled models maintain or exceed original model performance while being significantly more efficient and cost-effective to run.
                  </CardDescription>
                </CardContent>
              </Card>

              <Card className="feature-card glassmorphism-card">
                <CardHeader>
                  <div className="icon-container glassmorphism-icon">
                    <Zap size={24} style={{ color: '#00FFD1' }} />
                  </div>
                  <CardTitle className="heading-3">Flexible Deployment</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="body-medium">
                    Choose between on-premise deployment for maximum security or pay-per-use cloud solutions for scalability and convenience.
                  </CardDescription>
                </CardContent>
              </Card>

              <Card className="feature-card glassmorphism-card">
                <CardHeader>
                  <div className="icon-container glassmorphism-icon">
                    <Brain size={24} style={{ color: '#00FFD1' }} />
                  </div>
                  <CardTitle className="heading-3">LLM Expertise</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="body-medium">
                    Deep expertise in Small Large Language Models with proven track record in optimizing model performance for business applications.
                  </CardDescription>
                </CardContent>
              </Card>

              <Card className="feature-card glassmorphism-card">
                <CardHeader>
                  <div className="icon-container glassmorphism-icon">
                    <Eye size={24} style={{ color: '#00FFD1' }} />
                  </div>
                  <CardTitle className="heading-3">Vision Language Models</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="body-medium">
                    Specialized in Vision Language Models for multimodal applications that combine text and image understanding for your business needs.
                  </CardDescription>
                </CardContent>
              </Card>

              <Card className="feature-card glassmorphism-card">
                <CardHeader>
                  <div className="icon-container glassmorphism-icon">
                    <Clock size={24} style={{ color: '#00FFD1' }} />
                  </div>
                  <CardTitle className="heading-3">Live Deployments</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="body-medium">
                    Proven experience with live deployments using mixed model solutions, ensuring your AI systems work reliably in production environments.
                  </CardDescription>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Forward Deployed Engineers Section */}
      <section className="forward-engineers-section">
        <div className="dark-content-container">
          <div style={{ padding: '100px 0', textAlign: 'center' }}>
            <Badge className="glassmorphism-badge" style={{ marginBottom: '24px' }}>
              Unique Offering
            </Badge>
            <h2 className="display-medium" style={{ marginBottom: '24px' }}>
              Forward Deployed Engineers
            </h2>
            <p className="body-large" style={{ marginBottom: '60px', color: 'rgba(255, 255, 255, 0.85)', maxWidth: '800px', margin: '0 auto 60px' }}>
              Unlike traditional consulting, our engineers work directly within your environment, becoming an extension of your team while continuously enhancing your AI models.
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '40px', maxWidth: '1200px', margin: '0 auto' }}>
              <Card className="feature-card glassmorphism-card">
                <CardContent style={{ padding: '40px 32px', textAlign: 'center' }}>
                  <div className="icon-container glassmorphism-icon" style={{ margin: '0 auto 20px' }}>
                    <Brain size={28} style={{ color: '#00FFD1' }} />
                  </div>
                  <h3 className="heading-3" style={{ marginBottom: '16px' }}>Embedded Expertise</h3>
                  <p className="body-medium" style={{ color: 'rgba(255, 255, 255, 0.85)' }}>
                    Our engineers integrate seamlessly into your workflows, understanding your business context and technical constraints to deliver optimized solutions.
                  </p>
                </CardContent>
              </Card>

              <Card className="feature-card glassmorphism-card">
                <CardContent style={{ padding: '40px 32px', textAlign: 'center' }}>
                  <div className="icon-container glassmorphism-icon" style={{ margin: '0 auto 20px' }}>
                    <Zap size={28} style={{ color: '#00FFD1' }} />
                  </div>
                  <h3 className="heading-3" style={{ marginBottom: '16px' }}>Real-Time Enhancements</h3>
                  <p className="body-medium" style={{ color: 'rgba(255, 255, 255, 0.85)' }}>
                    Continuous model improvements based on real production data and user feedback, ensuring your AI evolves with your business needs.
                  </p>
                </CardContent>
              </Card>

              <Card className="feature-card glassmorphism-card">
                <CardContent style={{ padding: '40px 32px', textAlign: 'center' }}>
                  <div className="icon-container glassmorphism-icon" style={{ margin: '0 auto 20px' }}>
                    <ArrowRight size={28} style={{ color: '#00FFD1' }} />
                  </div>
                  <h3 className="heading-3" style={{ marginBottom: '16px' }}>Seamless Transfer</h3>
                  <p className="body-medium" style={{ color: 'rgba(255, 255, 255, 0.85)' }}>
                    When ready, complete knowledge and operational transfer to your internal team, or continue our services contract for ongoing innovation.
                  </p>
                </CardContent>
              </Card>
            </div>

            <div style={{ marginTop: '60px', padding: '40px', background: 'rgba(0, 255, 209, 0.05)', border: '1px solid rgba(0, 255, 209, 0.2)', borderRadius: '12px', backdropFilter: 'blur(10px)' }}>
              <h3 className="heading-2" style={{ color: '#00FFD1', marginBottom: '16px' }}>Why This Matters</h3>
              <p className="body-large" style={{ color: 'rgba(255, 255, 255, 0.9)', maxWidth: '700px', margin: '0 auto' }}>
                Traditional AI consulting delivers and leaves. Our forward deployed model ensures continuous optimization, faster iteration cycles, and seamless knowledge transfer - bridging the gap between external expertise and internal capability.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="pricing-section">
        <div className="dark-content-container">
          <div style={{ padding: '100px 0', textAlign: 'center' }}>
            <h2 className="display-medium" style={{ marginBottom: '16px' }}>
              Simple, Transparent Pricing
            </h2>
            <p className="body-large" style={{ marginBottom: '60px', color: 'rgba(255, 255, 255, 0.85)', maxWidth: '600px', margin: '0 auto 60px' }}>
              Choose the deployment option that fits your business needs and budget.
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '40px', maxWidth: '1000px', margin: '0 auto' }}>
              <Card className="pricing-card glassmorphism-card premium-glow">
                <CardHeader>
                  <div style={{ textAlign: 'center' }}>
                    <Badge className="glassmorphism-badge popular-badge">
                      Small Companies
                    </Badge>
                    <CardTitle className="heading-2" style={{ marginBottom: '8px' }}>Pay-Per-Inference</CardTitle>
                    <CardDescription className="body-medium">We build, you use & pay per call</CardDescription>
                  </div>
                </CardHeader>
                <CardContent style={{ textAlign: 'center' }}>
                  <div style={{ marginBottom: '24px' }}>
                    <span className="display-large" style={{ color: '#00FFD1' }}>Custom</span>
                    <div className="body-small" style={{ color: '#4D4D4D' }}>Per inference pricing</div>
                  </div>
                  <ul style={{ textAlign: 'left', marginBottom: '32px', listStyle: 'none', padding: 0 }}>
                    <li style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                      <CheckCircle size={16} style={{ color: '#00FFD1', marginRight: '12px' }} />
                      <span className="body-medium">Zero upfront model costs</span>
                    </li>
                    <li style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                      <CheckCircle size={16} style={{ color: '#00FFD1', marginRight: '12px' }} />
                      <span className="body-medium">We build & maintain the model</span>
                    </li>
                    <li style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                      <CheckCircle size={16} style={{ color: '#00FFD1', marginRight: '12px' }} />
                      <span className="body-medium">Scalable API access</span>
                    </li>
                    <li style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                      <CheckCircle size={16} style={{ color: '#00FFD1', marginRight: '12px' }} />
                      <span className="body-medium">Pay only for what you use</span>
                    </li>
                    <li style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                      <CheckCircle size={16} style={{ color: '#00FFD1', marginRight: '12px' }} />
                      <span className="body-medium">24/7 monitoring & support</span>
                    </li>
                  </ul>
                  <a className="btn-secondary" href="https://koalendar.com/u/64wMZ2tCwbeNQ3aNUVktnNVyFKl2" target="_blank" >
                    Schedule Consultation
                    <ArrowRight size={16} />
                  </a>
                </CardContent>
              </Card>

              <Card className="pricing-card glassmorphism-card">
                <CardHeader>
                  <div style={{ textAlign: 'center' }}>
                    <Badge className="glassmorphism-badge enterprise-badge my-2">
                      Enterprise
                    </Badge>
                    <CardTitle className="heading-2" style={{ marginBottom: '8px' }}>Build-Operate-Transfer</CardTitle>
                    <CardDescription className="body-medium">Full model ownership + services</CardDescription>
                  </div>
                </CardHeader>
                <CardContent style={{ textAlign: 'center' }}>
                  <div style={{ marginBottom: '24px' }}>
                    <span className="display-large" style={{ color: '#FFFFFF' }}>Custom</span>
                    <div className="body-small" style={{ color: '#4D4D4D' }}>Development + Services</div>
                  </div>
                  <ul style={{ textAlign: 'left', marginBottom: '32px', listStyle: 'none', padding: 0 }}>
                    <li style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                      <CheckCircle size={16} style={{ color: '#00FFD1', marginRight: '12px' }} />
                      <span className="body-medium">Model development fee</span>
                    </li>
                    <li style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                      <CheckCircle size={16} style={{ color: '#00FFD1', marginRight: '12px' }} />
                      <span className="body-medium">Full model ownership</span>
                    </li>
                    <li style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                      <CheckCircle size={16} style={{ color: '#00FFD1', marginRight: '12px' }} />
                      <span className="body-medium">Forward deployed engineers</span>
                    </li>
                    <li style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                      <CheckCircle size={16} style={{ color: '#00FFD1', marginRight: '12px' }} />
                      <span className="body-medium">Continuous enhancements</span>
                    </li>
                    <li style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                      <CheckCircle size={16} style={{ color: '#00FFD1', marginRight: '12px' }} />
                      <span className="body-medium">Knowledge transfer ready</span>
                    </li>
                  </ul>
                  <Button className="btn-secondary" style={{ width: '100%' }}>
                    Contact Sales
                    <Phone size={16} />
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact-section">
        <div className="dark-content-container">
          <div style={{ padding: '100px 0', textAlign: 'center' }}>
            <h2 className="display-medium" style={{ marginBottom: '16px' }}>
              Ready to Distill Your Business Data?
            </h2>
            <p className="body-large" style={{ marginBottom: '60px', color: 'rgba(255, 255, 255, 0.85)', maxWidth: '600px', margin: '0 auto 60px' }}>
              Get in touch with our experts to discuss how Vapour can optimize your AI models and reduce costs.
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '40px', marginBottom: '60px' }}>
              <Card className="contact-card glassmorphism-card">
                <CardContent style={{ padding: '32px', textAlign: 'center' }}>
                  <div className="icon-container glassmorphism-icon" style={{ margin: '0 auto 16px' }}>
                    <Mail size={24} style={{ color: '#00FFD1' }} />
                  </div>
                  <h3 className="heading-3" style={{ marginBottom: '8px' }}>Email Us</h3>
                  <p className="body-medium" style={{ color: 'rgba(255, 255, 255, 0.85)', marginBottom: '16px' }}>
                    Get a detailed proposal
                  </p>
                  <a href="mailto:vapour@nighthack.in" className="body-medium" style={{ color: '#00FFD1', textDecoration: 'none' }}>
                    vapour@nighthack.in
                  </a>
                </CardContent>
              </Card>

              <Card className="contact-card glassmorphism-card">
                <CardContent style={{ padding: '32px', textAlign: 'center' }}>
                  <div className="icon-container glassmorphism-icon" style={{ margin: '0 auto 16px' }}>
                    <Phone size={24} style={{ color: '#00FFD1' }} />
                  </div>
                  <h3 className="heading-3" style={{ marginBottom: '8px' }}>Schedule Call</h3>
                  <p className="body-medium" style={{ color: 'rgba(255, 255, 255, 0.85)', marginBottom: '16px' }}>
                    Book a consultation
                  </p>
                  <a href="https://koalendar.com/e/meet-with-vishrut-jha" target="_blank" className="btn-secondary">
                    Book Meeting
                    <ArrowRight size={16} />
                  </a>
                </CardContent>
              </Card>

              <Card className="contact-card glassmorphism-card">
                <CardContent style={{ padding: '32px', textAlign: 'center' }}>
                  <div className="icon-container glassmorphism-icon" style={{ margin: '0 auto 16px' }}>
                    <MapPin size={24} style={{ color: '#00FFD1' }} />
                  </div>
                  <h3 className="heading-3" style={{ marginBottom: '8px' }}>Visit Us</h3>
                  <p className="body-medium" style={{ color: 'rgba(255, 255, 255, 0.85)', marginBottom: '16px' }}>
                    NightHack Labs HQ
                  </p>
                  <p className="body-medium" style={{ color: 'rgba(255, 255, 255, 0.85)' }}>
                    San Francisco, CA
                  </p>
                </CardContent>
              </Card>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '60px', alignItems: 'start' }}>
              {/* Contact Form */}
              <div>
                <Card className="glassmorphism-card" style={{ padding: '0' }}>
                  <CardContent style={{ padding: '40px' }}>
                    <h3 className="heading-2" style={{ marginBottom: '24px', textAlign: 'center' }}>Get Custom Pricing</h3>
                    
                    <form name="vapour-contact" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }} netlify>
                      <input type="hidden" name="form-name" value="vapour-contact" />
                      <div style={{ display: 'none' }}>
                        <label>Don't fill this out if you're human: <input name="bot-field" /></label>
                      </div>
                      
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                        <div>
                          <label className="body-medium" style={{ display: 'block', marginBottom: '8px', color: 'rgba(255, 255, 255, 0.9)' }}>
                            First Name *
                          </label>
                          <input 
                            type="text" 
                            name="firstName" 
                            required 
                            className="contact-input"
                            style={{ 
                              width: '100%', 
                              padding: '12px 16px', 
                              background: 'rgba(255, 255, 255, 0.1)', 
                              border: '1px solid rgba(0, 255, 209, 0.3)', 
                              borderRadius: '8px', 
                              color: '#FFFFFF',
                              fontSize: '16px',
                              backdropFilter: 'blur(10px)'
                            }}
                          />
                        </div>
                        <div>
                          <label className="body-medium" style={{ display: 'block', marginBottom: '8px', color: 'rgba(255, 255, 255, 0.9)' }}>
                            Last Name *
                          </label>
                          <input 
                            type="text" 
                            name="lastName" 
                            required 
                            className="contact-input"
                            style={{ 
                              width: '100%', 
                              padding: '12px 16px', 
                              background: 'rgba(255, 255, 255, 0.1)', 
                              border: '1px solid rgba(0, 255, 209, 0.3)', 
                              borderRadius: '8px', 
                              color: '#FFFFFF',
                              fontSize: '16px',
                              backdropFilter: 'blur(10px)'
                            }}
                          />
                        </div>
                      </div>

                      <div>
                        <label className="body-medium" style={{ display: 'block', marginBottom: '8px', color: 'rgba(255, 255, 255, 0.9)' }}>
                          Email Address *
                        </label>
                        <input 
                          type="email" 
                          name="email" 
                          required 
                          className="contact-input"
                          style={{ 
                            width: '100%', 
                            padding: '12px 16px', 
                            background: 'rgba(255, 255, 255, 0.1)', 
                            border: '1px solid rgba(0, 255, 209, 0.3)', 
                            borderRadius: '8px', 
                            color: '#FFFFFF',
                            fontSize: '16px',
                            backdropFilter: 'blur(10px)'
                          }}
                        />
                      </div>

                      <div>
                        <label className="body-medium" style={{ display: 'block', marginBottom: '8px', color: 'rgba(255, 255, 255, 0.9)' }}>
                          Company
                        </label>
                        <input 
                          type="text" 
                          name="company" 
                          className="contact-input"
                          style={{ 
                            width: '100%', 
                            padding: '12px 16px', 
                            background: 'rgba(255, 255, 255, 0.1)', 
                            border: '1px solid rgba(0, 255, 209, 0.3)', 
                            borderRadius: '8px', 
                            color: '#FFFFFF',
                            fontSize: '16px',
                            backdropFilter: 'blur(10px)'
                          }}
                        />
                      </div>

                      <div>
                        <label className="body-medium" style={{ display: 'block', marginBottom: '8px', color: 'rgba(255, 255, 255, 0.9)' }}>
                          Pricing Model Interest
                        </label>
                        <select 
                          name="pricingModel" 
                          className="contact-input"
                          style={{ 
                            width: '100%', 
                            padding: '12px 16px', 
                            background: 'rgba(255, 255, 255, 0.1)', 
                            border: '1px solid rgba(0, 255, 209, 0.3)', 
                            borderRadius: '8px', 
                            color: '#FFFFFF',
                            fontSize: '16px',
                            backdropFilter: 'blur(10px)'
                          }}
                        >
                          <option value="" style={{ background: '#121212', color: '#FFFFFF' }}>Select an option</option>
                          <option value="pay-per-inference" style={{ background: '#121212', color: '#FFFFFF' }}>Pay-Per-Inference</option>
                          <option value="build-operate-transfer" style={{ background: '#121212', color: '#FFFFFF' }}>Build-Operate-Transfer</option>
                          <option value="both" style={{ background: '#121212', color: '#FFFFFF' }}>Both Options</option>
                        </select>
                      </div>

                      <div>
                        <label className="body-medium" style={{ display: 'block', marginBottom: '8px', color: 'rgba(255, 255, 255, 0.9)' }}>
                          Project Details
                        </label>
                        <textarea 
                          name="message" 
                          rows="4" 
                          className="contact-input"
                          placeholder="Tell us about your AI model requirements, data size, and expected usage..."
                          style={{ 
                            width: '100%', 
                            padding: '12px 16px', 
                            background: 'rgba(255, 255, 255, 0.1)', 
                            border: '1px solid rgba(0, 255, 209, 0.3)', 
                            borderRadius: '8px', 
                            color: '#FFFFFF',
                            fontSize: '16px',
                            backdropFilter: 'blur(10px)',
                            resize: 'vertical',
                            minHeight: '100px'
                          }}
                        />
                      </div>

                      <Button type="submit" className="btn-primary" style={{ width: '100%', padding: '16px' }}>
                        Get Custom Quote
                        <ArrowRight size={18} />
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </div>

              {/* Contact Information */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
                <Card className="contact-card glassmorphism-card">
                  <CardContent style={{ padding: '32px', textAlign: 'center' }}>
                    <div className="icon-container glassmorphism-icon" style={{ margin: '0 auto 16px' }}>
                      <Mail size={24} style={{ color: '#00FFD1' }} />
                    </div>
                    <h3 className="heading-3" style={{ marginBottom: '8px' }}>Email Us</h3>
                    <p className="body-medium" style={{ color: 'rgba(255, 255, 255, 0.85)', marginBottom: '16px' }}>
                      Direct communication
                    </p>
                    <a href="mailto:vapour@nighthacklabs.com" className="body-medium" style={{ color: '#00FFD1', textDecoration: 'none' }}>
                      vapour@nighthacklabs.com
                    </a>
                  </CardContent>
                </Card>

                <Card className="contact-card glassmorphism-card">
                  <CardContent style={{ padding: '32px', textAlign: 'center' }}>
                    <div className="icon-container glassmorphism-icon" style={{ margin: '0 auto 16px' }}>
                      <Phone size={24} style={{ color: '#00FFD1' }} />
                    </div>
                    <h3 className="heading-3" style={{ marginBottom: '8px' }}>Schedule Call</h3>
                    <p className="body-medium" style={{ color: 'rgba(255, 255, 255, 0.85)', marginBottom: '16px' }}>
                      Book a consultation
                    </p>
                    <Button className="btn-secondary">
                      Book Meeting
                      <ArrowRight size={16} />
                    </Button>
                  </CardContent>
                </Card>

                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                  <Button className="btn-primary" onClick={handleDemoClick} style={{ fontSize: '18px', padding: '14px 28px' }}>
                    See Our Models In Action
                    <ArrowRight size={18} />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ background: '#121212', borderTop: '1px solid rgba(255, 255, 255, 0.25)', padding: '40px 0' }}>
        <div className="dark-content-container">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <span className="heading-3" style={{ color: '#00FFD1' }}>Vapour</span>
              <span className="body-medium" style={{ color: '#4D4D4D', marginLeft: '8px' }}>by NightHack Labs</span>
            </div>
            <p className="body-small" style={{ color: '#4D4D4D' }}>
              © 2024 NightHack Labs. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default VapourLanding;